import MXModal from "@eisinc/App/Components/MXModal/MXModal";
import ScrollToAnchor from "@eisinc/App/Components/ScrollToAnchor/ScrollToAnchor";
import XngScriptInjector from "@eisinc/App/Components/XngScriptInjector/XngScriptInjector";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { HeaderSize } from "@eisinc/Overrides/Widgets/Header/LogoLinksSearch";
import { screen } from "@eisinc/XDL/Helpers";
import { getCookie, removeCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);

    return {
        isSticky: state.xHeader.isSticky,
        stickyMenu: state.xHeader.stickyMenu,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        location,
        isCartPage: getCurrentPage(state)?.name === "Cart",
        customSettings: getSettingsCollection(state).customSettings,
        isGlobalMessage: state.xHeader.globalMessage,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    header?: ContainerProps;
    container?: ContainerProps;
    navigation?: InjectableCss;
    navigationPlaceholder?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: 10;
            ${screen("lte", "sm")`
                position: fixed;
                top:0;
                width:100%;
            `}
        `,
    },
    navigation: {
        css: css`
            transition: top 350ms ease-in;
            background-color: ${getColor("text.main")};
            ${({ isSticky }: any) => isSticky && `position: fixed;top:-50px;width: 100%;`}
            ${({ stickyMenu }: any) => stickyMenu && `top:75px;`}
            ${screen("lte", "md")`
                display:none;
            `}
        `,
    },
    navigationPlaceholder: {
        css: css`
            ${({ isSticky }: any) => isSticky && `height:50px;`}
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            display: flex;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    stickyMenu,
    location,
    isCartPage,
    customSettings,
    isGlobalMessage,
    isTabletScreen,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const updateIframesSrc = () => {
        const utmCollection = getCookie("UtmCollection") ?? "";

        if (!utmCollection) {
            return;
        }

        const paramsFromCookie = new URLSearchParams(
            decodeURIComponent(utmCollection.replace(/%26/g, "&").replace(/%3D/g, "=")),
        );

        const iframes = document.querySelectorAll('iframe[id^="JotFormIFrame"]');

        if (!iframes.length) {
            return;
        }

        iframes.forEach((iframe: any) => {
            const iframeSrc = new URL(iframe.src);
            const iframeParams = new URLSearchParams(iframeSrc.search);

            paramsFromCookie.forEach((value, key) => {
                if (iframeParams.has(key)) {
                    iframeParams.set(key, value);
                } else {
                    iframeParams.append(key, value);
                }
            });

            iframeSrc.search = iframeParams.toString();
            iframe.src = iframeSrc.toString();
        });
    };

    const populateUtmCollectionCookie = () => {
        const utmCollectionSetting = (customSettings as any).utmCollection?.split(",");

        if (location && location.search.trim().length > 0 && location.search.indexOf(encodeURI("<script>")) === -1) {
            let utmParameters = location.search;
            if (utmParameters.startsWith("?")) {
                utmParameters = utmParameters.replace("?", "");
            }

            const filteredParams: Record<string, string> = {};

            const paramsArray = utmParameters.split("&");

            paramsArray.forEach(param => {
                const [key, value] = param.split("=");
                if (utmCollectionSetting?.includes(key)) {
                    filteredParams[key] = value;
                }
            });

            const filteredQueryString = Object.entries(filteredParams)
                .map(([key, value]) => `${key}=${value}`)
                .join("&");

            if (filteredQueryString) {
                setCookie("UtmCollection", filteredQueryString);
            }
        }
    };

    const updateBodyPadding = () => {
        if (!isTabletScreen) {
            return;
        }

        const baseHeaderHeight = HeaderSize.mobile;
        const additionalHeight = isGlobalMessage ? HeaderSize.gsm : 0;
        const totalHeaderHeight = baseHeaderHeight + additionalHeight;

        document.body.style.paddingTop = `${totalHeaderHeight}px`;
    };

    useEffect(() => {
        const timeoutId = setTimeout(updateIframesSrc, 400);
        return () => clearTimeout(timeoutId);
    }, [location.pathname]);

    // EIS-4106 - Update the body padding top when the global message is updated
    useEffect(() => {
        const timeoutId = setTimeout(updateBodyPadding, 0);
        return () => clearTimeout(timeoutId);
    }, [isGlobalMessage, isTabletScreen]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }

        populateUtmCollectionCookie();
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <StyledWrapper>
            <Page {...styles.header} as="header" isHeader={true}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="GlobalWebsiteMessage" />
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <StyledWrapper {...styles.navigationPlaceholder} {...({ isSticky, stickyMenu } as any)} />
                    <StyledWrapper {...styles.navigation} {...({ isSticky, stickyMenu } as any)}>
                        <Page {...styles.container}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Page>
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                <XngScriptInjector />
            </Page>
            <MXModal />
            <ScrollToAnchor />
        </StyledWrapper>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
